import LoadingScreen from 'components/LoadingScreen';
import ResendVerificationMail from 'containers/auth/ResendVerificationMail';
import PortalAlreadyConnected from 'containers/error/PortalAlreadyConnected';
import FormEditor from 'containers/forms/FormEditor';
import Gateway from 'containers/manage/gateways/Gateway';
import CallbackScreen from 'containers/onboarding/CallbackScreen';
import Onboarding from 'containers/onboarding/Onboarding';
import SetPassword from 'containers/onboarding/SetPassword';
import WebpackLoadError from 'containers/others/ErrorBoundary';
import Payments from 'containers/payments/PaymentsContainer';
import { EditProduct } from 'containers/products/EditProduct';
import UserAccount from 'containers/user/account/UserAccount';
import useAuth from 'hooks/useAuth';
import DashboardLayout from 'layouts/dashboard';
import { ElementType, lazy, Suspense } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
import { PATH_AFTER_LOGIN } from '../config';
import VerifyEmail from '../containers/auth/VerifyEmail';
import AuthGuard from '../guards/AuthGuard';
import GuestGuard from '../guards/GuestGuard';
import { PATH_USER } from './paths';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Loadable = (Component: ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isAuthenticated } = useAuth();
  const isDashboard = pathname.includes('/dashboard') && isAuthenticated;

  return (
    <WebpackLoadError>
      <Suspense fallback={<LoadingScreen isDashboard={isDashboard} />}>
        <Component {...props} />
      </Suspense>
    </WebpackLoadError>
  );
};

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../containers/auth/Login')));

const Register = Loadable(lazy(() => import('../containers/auth/Register')));

const Forms = Loadable(
  lazy(() => import('../containers/dashboard/Forms/FormListing'))
);
const Formsubmission = Loadable(
  lazy(
    () => import('../containers/dashboard/FormSubmission/FormSubmissionListing')
  )
);
const Dashboard = Loadable(
  lazy(() => import('../containers/dashboard/Dashboard'))
);

const QuotesEditor = Loadable(
  lazy(() => import('../containers/quotes/QuotesEditor'))
);

const QuoteListing = Loadable(
  lazy(() => import('../containers/quotes/QuoteListing'))
);

const Transactions = Loadable(
  lazy(() => import('../containers/dashboard/Transaction/Transactions'))
);
const TransactionListing = Loadable(
  lazy(() => import('../containers/dashboard/Transaction/TransactionListing'))
);

const SubscriptionListing = Loadable(
  lazy(() => import('../containers/dashboard/Transaction/SubscriptionListing'))
);

const GatewaysListing = Loadable(
  lazy(() => import('../containers/manage/gateways/GatewaysListing'))
);
const GatewaysEdit = Loadable(
  lazy(() => import('../containers/manage/gateways/GatewayEdit'))
);
const ResetPassword = Loadable(
  lazy(() => import('../containers/auth/ResetPassword'))
);
const ChangePassword = Loadable(
  lazy(() => import('../containers/auth/ChangePassword'))
);
const PortalListing = Loadable(
  lazy(() => import('../containers/user/Portal/PortalListing'))
);
const ConnectToHubspot = Loadable(
  lazy(() => import('../containers/user/ConnectHubspot/ConnectToHubspot'))
);
const VerifyCode = Loadable(
  lazy(() => import('../containers/auth/VerifyCode'))
);
const Products = Loadable(
  lazy(() => import('../containers/products/Products'))
);
const CreateProduct = Loadable(
  lazy(() => import('../containers/products/CreateProduct'))
);

const ProductListing = Loadable(
  lazy(() => import('../../src/containers/products/ProductListing'))
);

const Coupons = Loadable(lazy(() => import('../containers/coupons/Coupons')));
const CreateCoupon = Loadable(
  lazy(() => import('../containers/coupons/CreateCoupon'))
);
const CouponListing = Loadable(
  lazy(() => import('../containers/coupons/CouponListing'))
);

const EditCoupon = Loadable(
  lazy(() => import('../containers/coupons/EditCoupons'))
);

const ShareForm = Loadable(lazy(() => import('../containers/forms/ShareForm')));

export default function Router() {
  return useRoutes([
    {
      path: '/share/:formId',
      element: <ShareForm />,
    },
    {
      path: '/pay/:formId',
      element: <ShareForm />,
    },

    {
      path: 'onboarding',
      element: (
        <GuestGuard>
          <Onboarding />
        </GuestGuard>
      ),
    },

    {
      path: 'onboarding/set-password',
      element: <SetPassword />,
    },

    {
      path: 'onboarding/callback',
      element: <CallbackScreen />,
    },

    {
      path: 'auth',
      element: <GuestGuard />,

      children: [
        { path: 'login', element: <Login /> },
        { path: 'register', element: <Register /> },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'verify-mail', element: <VerifyCode /> },
        { path: 'resend-mail', element: <ResendVerificationMail /> },
        { path: 'verify-email', element: <VerifyEmail /> },
        { path: 'forgotreset/:token', element: <ChangePassword /> },
      ],
    },
    {
      path: '/',
      element: <AuthGuard />,

      children: [
        {
          path: '/user',
          children: [
            {
              path: 'portals',
              element: <PortalListing />,
            },
            {
              path: 'error/portal-already-connected',
              element: <PortalAlreadyConnected />,
            },
            {
              path: 'connect-to-hubspot',
              element: <ConnectToHubspot />,
            },
          ],
        },
        {
          path: ':portalId/user/settings',
          element: <DashboardLayout />,
          children: [
            {
              path: ':tab',
              index: true,
              element: <UserAccount />,
            },
          ],
        },
        {
          path: ':portalId',
          element: <DashboardLayout />,
          children: [
            {
              element: <Navigate to={PATH_AFTER_LOGIN} replace />,
              index: true,
            },
            { path: 'app', element: <Dashboard /> },
            { path: 'forms/list', element: <Forms /> },
            {
              path: 'products',
              element: <Products />,
              children: [
                {
                  index: true,
                  element: <ProductListing />,
                },
                {
                  path: 'create',
                  element: <CreateProduct />,
                },
                {
                  path: 'edit/:id',
                  element: <EditProduct />,
                },
              ],
            },
            {
              path: 'coupons',
              element: <Coupons />,
              children: [
                {
                  index: true,
                  element: <CouponListing />,
                },
                {
                  path: 'create',
                  element: <CreateCoupon />,
                },
                {
                  path: 'edit/:id',
                  element: <EditCoupon />,
                },
              ],
            },
            { path: 'formsubmission/list', element: <Formsubmission /> },
            {
              path: 'transactions',
              children: [
                {
                  index: true,
                  path: '',
                  element: <Navigate to='transactionlisting' />,
                },
                {
                  path: 'transactionlisting',
                  element: <TransactionListing />,
                },
                {
                  path: 'subscriptionlisting',
                  element: <SubscriptionListing />,
                },
              ],
            },
          ],
        },
        {
          path: ':portalId',
          element: <DashboardLayout />,
          children: [
            {
              path: 'gateways',
              element: <Gateway />,
              children: [
                {
                  index: true,
                  element: <GatewaysListing />,
                },
                {
                  path: 'edit/:gatewayId',
                  element: <GatewaysEdit />,
                },
              ],
            },
          ],
        },
        {
          path: ':portalId/forms',
          element: <DashboardLayout />,
          children: [
            {
              path: 'list',
              element: <Forms />,
            },
            {
              path: 'edit/:formId',
              element: <FormEditor />,
            },
          ],
        },
        {
          path: ':portalId/quotes',
          element: <DashboardLayout />,
          children: [
            {
              path: 'list',
              element: <QuoteListing />,
            },
            {
              path: 'edit/:quoteId',
              element: <QuotesEditor />,
            },
          ],
        },
        {
          path: ':portalId/formsubmission',
          element: <DashboardLayout />,
          children: [
            {
              path: 'list',
              element: <Formsubmission />,
            },
          ],
        },
        {
          path: ':portalId/user/payments',
          element: <Payments />,
        },

        { path: '', element: <Navigate to={PATH_USER.portals} /> },

        { path: '404', element: <NotFound /> },
        { path: '*', element: <NotFound /> },
      ],
    },
  ]);
}
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
